// ── The chat's TASK blocks, as pure rules (chat Figma rebuild WP3, frames 08–17) ───────────────
//
// Two answer blocks act on a task: `entityCreated` ("Task created · Buy a new iPhone", frames
// 08/12/16/17) and `suggestion` ("Suggested task … Create · View", frames 09–11). Everything they
// need to decide — the due line's wording, what "View Task" hands the box, how a saved task turns
// back into a block — is here, so the components and the store module only wire it up
// (components/Home/blocks/EntityCreatedBlock.vue, SuggestionBlock.vue, Home/ChatDock.vue,
// store/modules/chatActions.js).

import { dayDistance, shortClock } from '@/lib/timelineTime'
import { dateOnlyDay } from '@/lib/taskDue'
import { blockText } from '@/lib/answerBlocks'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * A task-srv date as an explicit UTC instant. The task service answers `dueDate` with NO zone
 * ("2026-10-07T00:00:00") while meaning UTC; read as-is, a browser east of Greenwich takes it as
 * local time and a date-only task's day slips back one ("Tomorrow" became "Today" after an update,
 * chat rebuild integration, frame 13). Anything already zoned passes through.
 */
export function serverUtc(value) {
  if (typeof value !== 'string') return value || null
  return /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value) ? `${value}Z` : value
}

/** A valid Date for an ISO string (a date-only task's day as LOCAL midnight), or null. */
function dueDate(dueUtc, allDay) {
  if (!dueUtc) return null
  const d = allDay ? dateOnlyDay(dueUtc) : new Date(dueUtc)
  return d && !isNaN(d.getTime()) ? d : null
}

/** "Today" / "Tomorrow" / "Yesterday", else "Oct 2" — and "Oct 2, 2027" outside this year. */
function dayWord(d, now) {
  const days = dayDistance(d, now)
  if (days === 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  if (days === -1) return 'Yesterday'
  const label = `${MONTHS[d.getMonth()]} ${d.getDate()}`
  return d.getFullYear() === new Date(now).getFullYear() ? label : `${label}, ${d.getFullYear()}`
}

/**
 * The due line of a created task (frames 08/12/13/16): "Unscheduled", "Tomorrow",
 * "Tomorrow, 12pm", "Oct 2", "Oct 2, 5pm". A date-only task never prints a clock; a timed one
 * always does. Month-first, as every chat frame writes it (the rail's "23 Jul" is a different
 * surface's spelling).
 */
export function createdDueLabel(dueUtc, allDay = false, now = Date.now()) {
  const d = dueDate(dueUtc, allDay)
  if (!d) return 'Unscheduled'
  const day = dayWord(d, now)
  return allDay ? day : `${day}, ${shortClock(d)}`
}

/** The kind's word for the block heads — "Task", "Note"… (`task` when the server sent none). */
export function kindWord(kind) {
  const k = String(kind || 'task')
  return k[0].toUpperCase() + k.slice(1)
}

/** Is this block (by turn and key) the one docked in the box right now? */
export function isDockedBlock(dockRef, turnId, key) {
  return !!dockRef && dockRef.turnId === turnId && dockRef.key === key
}

/**
 * What "View Task" / "View" hands the box (`smartbox-edit-entity`).
 *
 *   • A CREATED task opens by id, straight into the edit state (frame 14: "Update task", grey until
 *     something changes — the box's own baseline rule). Its body and flags are read by the box from
 *     the task itself (SmartBox `loadEditBody` / `seedEntityState`), so only what the block knows
 *     travels.
 *   • A SUGGESTION has no id, so the box opens it as a draft to create (frame 10: "Create Task",
 *     green), title and due date filled.
 */
export function dockEditDetail(block) {
  const b = block || {}
  const base = {
    menu: 'tasks',
    title: String(b.title || ''),
    dueDate: b.dueUtc || '',
    isAllDay: !!b.allDay,
  }
  if (b.type === 'entityCreated' && b.id) return { ...base, id: String(b.id), edit: true }
  return { ...base, content: b.detail ? String(b.detail) : '' }
}

/** The block a suggestion becomes once its task exists (frame 11), from the saved task. */
export function createdFromTask(task, suggestion = {}, ongoing = null) {
  const t = task || {}
  return {
    type: 'entityCreated',
    kind: suggestion.kind || 'task',
    id: t.id ? String(t.id) : null,
    title: String(t.title || suggestion.title || ''),
    dueUtc: t.dueDate !== undefined ? serverUtc(t.dueDate) : (suggestion.dueUtc || null),
    allDay: t.isAllDay !== undefined ? !!t.isAllDay : !!suggestion.allDay,
    ongoing: ongoing && ongoing.id ? { id: String(ongoing.id), title: String(ongoing.title || ''), module: ongoing.module || ongoing.origin || null } : null,
    state: null,
  }
}

/** A re-read task's fields as a patch on its created block (frame 13: the title and due move in place). */
export function createdPatchFromTask(task) {
  if (!task || !task.id) return null
  return {
    title: String(task.title || ''),
    dueUtc: serverUtc(task.dueDate),
    allDay: !!task.isAllDay,
  }
}

/**
 * The same, from the JSON patch the box just sent (`task/editTask` [id, ops]) — so the block moves
 * the instant the update lands, before the re-read answers (and when it never does).
 */
export function createdPatchFromOps(ops) {
  const out = {}
  for (const op of Array.isArray(ops) ? ops : []) {
    if (!op || op.op !== 'replace') continue
    if (op.path === '/title' && op.value) out.title = String(op.value)
    else if (op.path === '/dueDate') out.dueUtc = serverUtc(op.value)
    else if (op.path === '/isAllDay') out.allDay = !!op.value
  }
  return Object.keys(out).length ? out : null
}

/** The patch with the block's plain text brought along (what Copy answer and a stub would read). */
export function withFallbackText(block, patch) {
  const next = { ...(block || {}), ...(patch || {}) }
  return { ...(patch || {}), fallbackText: blockText(next) }
}

/** The stored message id of the answer a turn is — a restored turn keeps it apart from the question's. */
export function answerMessageId(turn) {
  const t = turn || {}
  return t.answerMessageId || t.messageId || null
}

/** Was the turn asked in THIS conversation, recently enough that what it made is still news? */
export function isRecentTurn(turn, now = Date.now(), windowMs = 24 * 60 * 60 * 1000) {
  const at = Date.parse(turn?.askedUtc || '')
  return !isNaN(at) && now - at >= 0 && now - at < windowMs
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Only a real server id can be posted to: a fixture's or a guest's local id could only 4xx. */
export function isServerId(id) {
  return GUID.test(String(id || ''))
}
